import { useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User as OidcUser } from 'oidc-client-ts';
import { keycloakService } from './keycloakService';
import type { User as AppUser } from '../types/user';
import { AuthContext, type AuthContextType } from './authContext';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mapOidcUserToAppUser = (oidcUser: OidcUser | null): AppUser | null => {
    if (!oidcUser) {
      return null;
    }

    const profile = oidcUser.profile || {};
    let roles: string[] = [];
    let tokenClaims: any = null;

    // ALWAYS decode token to get roles (Keycloak roles are in token, not profile)
    // CRITICAL: In Keycloak, roles are in ACCESS_TOKEN, NOT ID_TOKEN!
    // oidc-client-ts stores both tokens, but roles are only in access_token
    
    // Try access_token FIRST (roles are here!)
    if (oidcUser.access_token) {
      try {
        const base64Url = oidcUser.access_token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const padding = base64.length % 4;
        const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64;
        
        const jsonPayload = decodeURIComponent(
          atob(paddedBase64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        tokenClaims = JSON.parse(jsonPayload);
        roles = tokenClaims.realm_access?.roles || tokenClaims.roles || [];
      } catch (e) {
        // Failed to decode access_token - will try id_token as fallback
      }
    }
    
    // If no roles found in access_token, try id_token (unlikely to have roles in Keycloak)
    if (roles.length === 0 && oidcUser.id_token) {
      try {
        const base64Url = oidcUser.id_token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const padding = base64.length % 4;
        const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64;
        
        const jsonPayload = decodeURIComponent(
          atob(paddedBase64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const idTokenClaims = JSON.parse(jsonPayload);
        const idTokenRoles = idTokenClaims.realm_access?.roles || idTokenClaims.roles || [];
        
        if (idTokenRoles.length > 0) {
          roles = idTokenRoles;
          tokenClaims = idTokenClaims; // Use id_token claims for other user info
        }
      } catch (e) {
        // Failed to decode id_token
      }
    }
    
    // Get user info from id_token (has profile info like name, email, etc.)
    // But we already extracted roles from access_token above
    let idTokenClaims: any = null;
    if (oidcUser.id_token) {
      try {
        const idTokenBase64Url = oidcUser.id_token.split('.')[1];
        const idTokenBase64 = idTokenBase64Url.replace(/-/g, '+').replace(/_/g, '/');
        const idTokenPadding = idTokenBase64.length % 4;
        const idTokenPadded = idTokenPadding ? idTokenBase64 + '='.repeat(4 - idTokenPadding) : idTokenBase64;
        const idTokenPayload = decodeURIComponent(
          atob(idTokenPadded)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        idTokenClaims = JSON.parse(idTokenPayload);
      } catch (e) {
        // Failed to decode id_token for user info
      }
    }
    
    // Use id_token claims for user info (name, email, etc.), but roles come from access_token
    if (idTokenClaims && !tokenClaims) {
      tokenClaims = idTokenClaims; // Fallback if access_token wasn't decoded
    } else if (tokenClaims && idTokenClaims) {
      // Merge: roles from access_token, user info from id_token
      tokenClaims = { ...idTokenClaims, realm_access: tokenClaims.realm_access };
    }
    
    // If no tokens at all, fallback to profile
    if (!oidcUser.access_token && !oidcUser.id_token) {
      roles = (profile as any).realm_access?.roles || (profile as any).roles || [];
    }
    
    // Use token claims if available, otherwise fall back to profile
    return {
      sub: tokenClaims?.sub || profile.sub || '',
      email: tokenClaims?.email || profile.email,
      name: tokenClaims?.name || profile.name,
      given_name: tokenClaims?.given_name || profile.given_name,
      family_name: tokenClaims?.family_name || profile.family_name,
      preferred_username: tokenClaims?.preferred_username || profile.preferred_username,
      email_verified: tokenClaims?.email_verified ?? profile.email_verified,
      roles,
    };
  };

  const refreshUser = useCallback(async (isBackgroundRefresh = false) => {
    try {
      // Only show loading during initial load or manual refresh, NOT during periodic
      // background refresh. Setting loading=true unmounts protected routes (e.g.
      // CreatePostPage), which wipes form state and feels like a "reload".
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      setError(null);
      // Force token refresh to get latest roles
      await keycloakService.refreshToken();
      const oidcUser = await keycloakService.getUser();
      const appUser = mapOidcUserToAppUser(oidcUser);
      setUser(appUser);
      setIsAuthenticated(oidcUser !== null && !oidcUser.expired);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to refresh user';
      setError(errorMessage);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        const oidcUser = await keycloakService.init();
        const appUser = mapOidcUserToAppUser(oidcUser);
        setUser(appUser);
        setIsAuthenticated(oidcUser !== null && !oidcUser.expired);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize auth';
        setError(errorMessage);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []); // Run only once on mount

  useEffect(() => {
    // Set up automatic token renewal (background only – do not set loading
    // or we unmount protected routes and lose form state e.g. on /posts/new)
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      await refreshUser(true);
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUser]);

  // Cross-tab auth sync: oidc-client-ts persists the user in localStorage
  // under "oidc.user:<authority>:<client>". When another tab logs in (e.g. a
  // login window opened from CreatePostPage), that key changes and the
  // browser fires a "storage" event in this tab. Refresh in the background
  // so the UI picks up the new authenticated state without a full reload.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (!event.key || !event.key.startsWith('oidc.user:')) return;
      if (event.newValue === event.oldValue) return;
      console.debug('[AuthProvider] cross-tab oidc.user change, refreshing', {
        key: event.key,
        hadOld: !!event.oldValue,
        hasNew: !!event.newValue,
      });
      refreshUser(true);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshUser]);

  // Expose refreshUser for manual refresh
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).refreshAuth = async () => {
        await refreshUser();
      };
    }
  }, [refreshUser]);

  const login = async (redirectPath?: string) => {
    try {
      setError(null);
      await keycloakService.login(redirectPath);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await keycloakService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Logout failed';
      setError(errorMessage);
      throw err;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    refreshUser,
  };

  // Don't block rendering while loading - show children immediately
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

